/**
 * Standard toolkit hook for implementing a manual task processor
 **/

import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useManualTask } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTask'
import { useWatchtowerListPath } from 'Automations/watchtower/inbox/hooks/useWatchtowerListPath'
import { useIsMobile } from 'hooks/useIsMobile'
import invariant from 'tiny-invariant'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { ManualTask } from 'Automations/watchtower/inbox/manualTasks/types'
import { useCurrentUser } from 'common/hooks'
import { useSetAtom } from 'jotai'
import { manualTaskProcessorTaskIdAtom } from 'Automations/watchtower/atoms'

export const useManualTaskProcessorToolkit = <TInput>({
  taskId
}: {
  taskId: string
}): {
  manualTask: ManualTask<TInput> | undefined
  isPending: boolean
  handleClose: () => void
  completeTask: (outcome: any) => Promise<boolean>
} => {
  const currentUser = useCurrentUser()
  const setProcessorTaskId = useSetAtom(manualTaskProcessorTaskIdAtom)
  const navigate = useNavigate()
  const listPath = useWatchtowerListPath()
  const isMobile = useIsMobile()

  invariant(taskId, 'taskId is required')

  const { manualTask, isPending } = useManualTask({
    taskId
  })

  const handleClose = useCallback(() => {
    setProcessorTaskId(null)
  }, [setProcessorTaskId])

  const completeTask = useCallback(
    async (outcome: any) => {
      const { error } = await dashboardv99990101Client.postCompleteManualTask({
        id: taskId,
        outputData: {
          ...outcome,
          completedBy: currentUser.id
        }
      })

      if (error) {
        return false
      }

      setProcessorTaskId(null)

      // Desktop moves the selection on from the list, which mobile's single
      // pane doesn't mount behind a detail. Return to the list instead.
      if (isMobile) {
        navigate(listPath)
      }

      return true
    },
    [setProcessorTaskId, taskId, currentUser.id, isMobile, navigate, listPath]
  )

  return {
    manualTask: manualTask as ManualTask<TInput> | undefined,
    isPending,
    handleClose,
    completeTask
  }
}
